/**
 * ErrorMessage Component
 *
 * Displays error, warning, info, or success messages with appropriate styling.
 *
 * Props:
 * - visible: Whether the message is visible (default: true if message exists)
 * - message: The message to display (required)
 * - type: Message type (error, warning, info, success)
 * - title: Optional title/heading
 * - dismissible: Show close button (default: true)
 * - onDismiss: Callback when dismissed
 * - autoDismissTime: Auto-dismiss after milliseconds (0 = no auto-dismiss)
 * - position: Positioning (default, top-right, bottom-center, etc.)
 * - className: Custom CSS classes
 */

import { useState, useEffect } from 'react';
import { COPY } from '../copy';

export default function ErrorMessage({
  visible = false,
  message,
  type = 'error',
  title = '',
  dismissible = true,
  onDismiss,
  autoDismissTime = 0,
  position = 'top-right',
  className = '',
}) {
  const [isDismissed, setIsDismissed] = useState(false);

  // Auto-dismiss timer
  useEffect(() => {
    if (!visible || !autoDismissTime || autoDismissTime === 0) return;

    const timer = setTimeout(() => {
      if (onDismiss) {
        onDismiss();
      }
      setIsDismissed(true);
    }, autoDismissTime);

    return () => clearTimeout(timer);
  }, [visible, autoDismissTime, onDismiss]);

  // Don't render if not visible, no message, or dismissed
  if (!visible || !message || isDismissed) {
    return null;
  }

  // Type configurations
  const typeConfig = {
    error: {
      bgColor: 'bg-red-100',
      borderColor: 'border-red-500',
      textColor: 'text-red-800',
      icon: '❌',
      ariaLive: 'assertive',
    },
    warning: {
      bgColor: 'bg-amber-100',
      borderColor: 'border-amber-500',
      textColor: 'text-amber-800',
      icon: '⚠️',
      ariaLive: 'polite',
    },
    info: {
      bgColor: 'bg-blue-100',
      borderColor: 'border-blue-500',
      textColor: 'text-blue-800',
      icon: 'ℹ️',
      ariaLive: 'polite',
    },
    success: {
      bgColor: 'bg-green-100',
      borderColor: 'border-green-500',
      textColor: 'text-green-800',
      icon: '✅',
      ariaLive: 'polite',
    },
  };

  const config = typeConfig[type] || typeConfig.error;

  const handleDismiss = () => {
    setIsDismissed(true);
    if (onDismiss) {
      onDismiss();
    }
  };

  const ariaLabel = title
    ? `${type}: ${title}. ${message}`
    : `${type}: ${message}`;

  // Position classes
  const positionClasses = {
    default: '',
    'top-right': 'fixed top-4 right-4',
    'top-left': 'fixed top-4 left-4',
    'bottom-right': 'fixed bottom-4 right-4',
    'bottom-left': 'fixed bottom-4 left-4',
    'bottom-center': 'fixed bottom-4 left-1/2 transform -translate-x-1/2',
    'top-center': 'fixed top-4 left-1/2 transform -translate-x-1/2',
  };

  const positionClass = positionClasses[position] || positionClasses.default;

  // Add role based on type
  const role = type === 'info' ? 'status' : 'alert';

  return (
    <div
      data-testid="error-message"
      role={role}
      aria-live={config.ariaLive}
      aria-label={ariaLabel}
      className={`${config.bgColor} ${config.borderColor} ${config.textColor} border-l-4 p-4 rounded transition-all duration-300 break-words ${positionClass} ${className}`}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <span className="text-xl flex-shrink-0" aria-hidden="true">
          {config.icon}
        </span>

        {/* Content */}
        <div className="flex-grow">
          {title && <p className="font-bold text-lg mb-1">{title}</p>}
          <p className="text-sm">{message}</p>
        </div>

        {/* Dismiss button */}
        {dismissible && (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={COPY.errors.message.close}
            className="flex-shrink-0 text-xl hover:opacity-70 transition-opacity"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}
